import { cva } from 'class-variance-authority';

export const iconButtonVariants = cva(
  [
    'inline-flex',
    'items-center',
    'justify-center',
    'shrink-0',
    'rounded-radius-comp-button-icon',
    'focus-visible:outline',
    'focus-visible:outline-offset-2',
    'focus-visible:outline-2',
    'focus-visible:outline-color-ref-ui-focus-default',
    'data-[loading="true"]:pointer-events-none',
    '[&>svg]:fill-current',
    // common lib overrides are giving the button a default border on focus
    'focus:outline-none',
  ],
  {
    variants: {
      size: {
        tiny: [
          'h-sizing-comp-button-icon-tiny-min-height',
          'w-sizing-comp-button-icon-tiny-max-width',
          'px-spacing-comp-button-icon-tiny-horizontal',
          'py-spacing-comp-button-icon-tiny-vertical',
        ],
        small: [
          'h-sizing-comp-button-icon-small-min-height',
          'w-sizing-comp-button-icon-small-max-width',
          'px-spacing-comp-button-icon-small-horizontal',
          'py-spacing-comp-button-icon-small-vertical',
        ],
        medium: [
          'h-sizing-comp-button-icon-medium-min-height',
          'w-sizing-comp-button-icon-medium-max-width',
          'px-spacing-comp-button-icon-medium-horizontal',
          'py-spacing-comp-button-icon-medium-vertical',
        ],
      },
      disabled: {
        true: ['pointer-events-none'],
        false: '',
      },
      loading: {
        true: ['pointer-events-none'],
        false: '',
      },
      variant: {
        primary: '',
        success: '',
        danger: '',
        neutral: '',
        strong: '',
        inverted: '',
      },
      type: {
        filled: '',
        outline: 'ring-2 ring-inset',
        ghost: '',
      },
      selected: { true: '', false: '' },
    },
    compoundVariants: [
      // primary / filled
      {
        variant: 'primary',
        type: 'filled',
        disabled: false,
        selected: false,
        className: [
          'bg-color-comp-button-primary-solid-container-enabled',
          'hover:bg-color-comp-button-primary-solid-container-hover',
          'active:bg-color-comp-button-primary-solid-container-pressed',

          'text-color-comp-button-primary-solid-on-container-enabled',
          'hover:text-color-comp-button-primary-solid-on-container-hover',
          'active:text-color-comp-button-primary-solid-on-container-pressed',
        ],
      },
      {
        variant: 'primary',
        type: 'filled',
        disabled: true,
        selected: false,
        className: [
          'bg-color-comp-button-primary-solid-container-disabled',
          'text-color-comp-button-primary-solid-on-container-disabled',
        ],
      },
      {
        variant: 'primary',
        type: 'filled',
        disabled: false,
        selected: true,
        className: [
          'bg-color-comp-button-primary-solid-container-selected',
          'text-color-comp-button-primary-solid-on-container-selected',
        ],
      },
      // primary / outline
      {
        variant: 'primary',
        type: 'outline',
        disabled: false,
        selected: false,
        className: [
          'bg-color-comp-button-primary-plain-container-enabled',
          'hover:bg-color-comp-button-primary-plain-container-hover',
          'active:bg-color-comp-button-primary-plain-container-pressed',

          'text-color-comp-button-primary-plain-on-container-enabled',
          'hover:text-color-comp-button-primary-plain-on-container-hover',
          'active:text-color-comp-button-primary-plain-on-container-pressed',

          'ring-color-comp-button-primary-plain-border-enabled',
          'hover:ring-color-comp-button-primary-plain-border-hover',
          'active:ring-color-comp-button-primary-plain-border-pressed',
        ],
      },
      {
        variant: 'primary',
        type: 'outline',
        disabled: true,
        selected: false,
        className: [
          'bg-color-comp-button-primary-plain-container-disabled',
          'text-color-comp-button-primary-plain-on-container-disabled',
          'ring-color-comp-button-primary-plain-border-disabled',
        ],
      },
      {
        variant: 'primary',
        type: 'outline',
        disabled: false,
        selected: true,
        className: [
          'bg-color-comp-button-primary-plain-container-selected',
          'text-color-comp-button-primary-plain-on-container-selected',
          'ring-color-comp-button-primary-plain-border-selected',
        ],
      },
      // primary / ghost
      {
        variant: 'primary',
        type: 'ghost',
        disabled: false,
        selected: false,
        className: [
          'bg-color-comp-button-primary-plain-container-enabled',
          'hover:bg-color-comp-button-primary-plain-container-hover',
          'active:bg-color-comp-button-primary-plain-container-pressed',

          'text-color-comp-button-primary-plain-on-container-enabled',
          'hover:text-color-comp-button-primary-plain-on-container-hover',
          'active:text-color-comp-button-primary-plain-on-container-pressed',
        ],
      },
      {
        variant: 'primary',
        type: 'ghost',
        disabled: true,
        selected: false,
        className: [
          'bg-color-comp-button-primary-plain-container-disabled',
          'text-color-comp-button-primary-plain-on-container-disabled',
        ],
      },
      {
        variant: 'primary',
        type: 'ghost',
        disabled: false,
        selected: true,
        className: [
          'bg-color-comp-button-primary-plain-container-selected',
          'text-color-comp-button-primary-plain-on-container-selected',
        ],
      },
      // success / filled
      {
        variant: 'success',
        type: 'filled',
        disabled: false,
        selected: false,
        className: [
          'bg-color-comp-button-success-solid-container-enabled',
          'hover:bg-color-comp-button-success-solid-container-hover',
          'active:bg-color-comp-button-success-solid-container-pressed',

          'text-color-comp-button-success-solid-on-container-enabled',
          'hover:text-color-comp-button-success-solid-on-container-hover',
          'active:text-color-comp-button-success-solid-on-container-pressed',
        ],
      },
      {
        variant: 'success',
        type: 'filled',
        disabled: true,
        selected: false,
        className: [
          'bg-color-comp-button-success-solid-container-disabled',
          'text-color-comp-button-success-solid-on-container-disabled',
        ],
      },
      {
        variant: 'success',
        type: 'filled',
        disabled: false,
        selected: true,
        className: [
          'bg-color-comp-button-success-solid-container-selected',
          'text-color-comp-button-success-solid-on-container-selected',
        ],
      },
      // success / outline
      {
        variant: 'success',
        type: 'outline',
        disabled: false,
        selected: false,
        className: [
          'bg-color-comp-button-success-plain-container-enabled',
          'hover:bg-color-comp-button-success-plain-container-hover',
          'active:bg-color-comp-button-success-plain-container-pressed',

          'text-color-comp-button-success-plain-on-container-enabled',
          'hover:text-color-comp-button-success-plain-on-container-hover',
          'active:text-color-comp-button-success-plain-on-container-pressed',

          'ring-color-comp-button-success-plain-border-enabled',
          'hover:ring-color-comp-button-success-plain-border-hover',
          'active:ring-color-comp-button-success-plain-border-pressed',
        ],
      },
      {
        variant: 'success',
        type: 'outline',
        disabled: true,
        selected: false,
        className: [
          'bg-color-comp-button-success-plain-container-disabled',
          'text-color-comp-button-success-plain-on-container-disabled',
          'ring-color-comp-button-success-plain-border-disabled',
        ],
      },
      {
        variant: 'success',
        type: 'outline',
        disabled: false,
        selected: true,
        className: [
          'bg-color-comp-button-success-plain-container-selected',
          'text-color-comp-button-success-plain-on-container-selected',
          'ring-color-comp-button-success-plain-border-selected',
        ],
      },
      // success / ghost
      {
        variant: 'success',
        type: 'ghost',
        disabled: false,
        selected: false,
        className: [
          'bg-color-comp-button-success-plain-container-enabled',
          'hover:bg-color-comp-button-success-plain-container-hover',
          'active:bg-color-comp-button-success-plain-container-pressed',

          'text-color-comp-button-success-plain-on-container-enabled',
          'hover:text-color-comp-button-success-plain-on-container-hover',
          'active:text-color-comp-button-success-plain-on-container-pressed',
        ],
      },
      {
        variant: 'success',
        type: 'ghost',
        disabled: true,
        selected: false,
        className: [
          'bg-color-comp-button-success-plain-container-disabled',
          'text-color-comp-button-success-plain-on-container-disabled',
        ],
      },
      {
        variant: 'success',
        type: 'ghost',
        disabled: false,
        selected: true,
        className: [
          'bg-color-comp-button-success-plain-container-selected',
          'text-color-comp-button-success-plain-on-container-selected',
        ],
      },
      // danger / filled
      {
        variant: 'danger',
        type: 'filled',
        disabled: false,
        selected: false,
        className: [
          'bg-color-comp-button-danger-solid-container-enabled',
          'hover:bg-color-comp-button-danger-solid-container-hover',
          'active:bg-color-comp-button-danger-solid-container-pressed',

          'text-color-comp-button-danger-solid-on-container-enabled',
          'hover:text-color-comp-button-danger-solid-on-container-hover',
          'active:text-color-comp-button-danger-solid-on-container-pressed',
        ],
      },
      {
        variant: 'danger',
        type: 'filled',
        disabled: true,
        selected: false,
        className: [
          'bg-color-comp-button-danger-solid-container-disabled',
          'text-color-comp-button-danger-solid-on-container-disabled',
        ],
      },
      {
        variant: 'danger',
        type: 'filled',
        disabled: false,
        selected: true,
        className: [
          'bg-color-comp-button-danger-solid-container-selected',
          'text-color-comp-button-danger-solid-on-container-selected',
        ],
      },
      // danger / outline
      {
        variant: 'danger',
        type: 'outline',
        disabled: false,
        selected: false,
        className: [
          'bg-color-comp-button-danger-plain-container-enabled',
          'hover:bg-color-comp-button-danger-plain-container-hover',
          'active:bg-color-comp-button-danger-plain-container-pressed',

          'text-color-comp-button-danger-plain-on-container-enabled',
          'hover:text-color-comp-button-danger-plain-on-container-hover',
          'active:text-color-comp-button-danger-plain-on-container-pressed',

          'ring-color-comp-button-danger-plain-border-enabled',
          'hover:ring-color-comp-button-danger-plain-border-hover',
          'active:ring-color-comp-button-danger-plain-border-pressed',
        ],
      },
      {
        variant: 'danger',
        type: 'outline',
        disabled: true,
        selected: false,
        className: [
          'bg-color-comp-button-danger-plain-container-disabled',
          'text-color-comp-button-danger-plain-on-container-disabled',
          'ring-color-comp-button-danger-plain-border-disabled',
        ],
      },
      {
        variant: 'danger',
        type: 'outline',
        disabled: false,
        selected: true,
        className: [
          'bg-color-comp-button-danger-plain-container-selected',
          'text-color-comp-button-danger-plain-on-container-selected',
          'ring-color-comp-button-danger-plain-border-selected',
        ],
      },
      // danger / ghost
      {
        variant: 'danger',
        type: 'ghost',
        disabled: false,
        selected: false,
        className: [
          'bg-color-comp-button-danger-plain-container-enabled',
          'hover:bg-color-comp-button-danger-plain-container-hover',
          'active:bg-color-comp-button-danger-plain-container-pressed',

          'text-color-comp-button-danger-plain-on-container-enabled',
          'hover:text-color-comp-button-danger-plain-on-container-hover',
          'active:text-color-comp-button-danger-plain-on-container-pressed',
        ],
      },
      {
        variant: 'danger',
        type: 'ghost',
        disabled: true,
        selected: false,
        className: [
          'bg-color-comp-button-danger-plain-container-disabled',
          'text-color-comp-button-danger-plain-on-container-disabled',
        ],
      },
      {
        variant: 'danger',
        type: 'ghost',
        disabled: false,
        selected: true,
        className: [
          'bg-color-comp-button-danger-plain-container-selected',
          'text-color-comp-button-danger-plain-on-container-selected',
        ],
      },
      // neutral / filled
      {
        variant: 'neutral',
        type: 'filled',
        disabled: false,
        selected: false,
        className: [
          'bg-color-comp-button-neutral-solid-container-enabled',
          'hover:bg-color-comp-button-neutral-solid-container-hover',
          'active:bg-color-comp-button-neutral-solid-container-pressed',

          'text-color-comp-button-neutral-solid-on-container-enabled',
          'hover:text-color-comp-button-neutral-solid-on-container-hover',
          'active:text-color-comp-button-neutral-solid-on-container-pressed',
        ],
      },
      {
        variant: 'neutral',
        type: 'filled',
        disabled: true,
        selected: false,
        className: [
          'bg-color-comp-button-neutral-solid-container-disabled',
          'text-color-comp-button-neutral-solid-on-container-disabled',
        ],
      },
      {
        variant: 'neutral',
        type: 'filled',
        disabled: false,
        selected: true,
        className: [
          'bg-color-comp-button-neutral-solid-container-selected',
          'text-color-comp-button-neutral-solid-on-container-selected',
        ],
      },
      // neutral / outline
      {
        variant: 'neutral',
        type: 'outline',
        disabled: false,
        selected: false,
        className: [
          'bg-color-comp-button-neutral-plain-container-enabled',
          'hover:bg-color-comp-button-neutral-plain-container-hover',
          'active:bg-color-comp-button-neutral-plain-container-pressed',

          'text-color-comp-button-neutral-plain-on-container-enabled',
          'hover:text-color-comp-button-neutral-plain-on-container-hover',
          'active:text-color-comp-button-neutral-plain-on-container-pressed',

          'ring-color-comp-button-neutral-plain-border-enabled',
          'hover:ring-color-comp-button-neutral-plain-border-hover',
          'active:ring-color-comp-button-neutral-plain-border-pressed',
        ],
      },
      {
        variant: 'neutral',
        type: 'outline',
        disabled: true,
        selected: false,
        className: [
          'bg-color-comp-button-neutral-plain-container-disabled',
          'text-color-comp-button-neutral-plain-on-container-disabled',
          'ring-color-comp-button-neutral-plain-border-disabled',
        ],
      },
      {
        variant: 'neutral',
        type: 'outline',
        disabled: false,
        selected: true,
        className: [
          'bg-color-comp-button-neutral-plain-container-selected',
          'text-color-comp-button-neutral-plain-on-container-selected',
          'ring-color-comp-button-neutral-plain-border-selected',
        ],
      },
      // neutral / ghost
      {
        variant: 'neutral',
        type: 'ghost',
        disabled: false,
        selected: false,
        className: [
          'bg-color-comp-button-neutral-plain-container-enabled',
          'hover:bg-color-comp-button-neutral-plain-container-hover',
          'active:bg-color-comp-button-neutral-plain-container-pressed',

          'text-color-comp-button-neutral-plain-on-container-enabled',
          'hover:text-color-comp-button-neutral-plain-on-container-hover',
          'active:text-color-comp-button-neutral-plain-on-container-pressed',
        ],
      },
      {
        variant: 'neutral',
        type: 'ghost',
        disabled: true,
        selected: false,
        className: [
          'bg-color-comp-button-neutral-plain-container-disabled',
          'text-color-comp-button-neutral-plain-on-container-disabled',
        ],
      },
      {
        variant: 'neutral',
        type: 'ghost',
        disabled: false,
        selected: true,
        className: [
          'bg-color-comp-button-neutral-plain-container-selected',
          'text-color-comp-button-neutral-plain-on-container-selected',
        ],
      },
      // strong / filled
      {
        variant: 'strong',
        type: 'filled',
        disabled: false,
        selected: false,
        className: [
          'bg-color-comp-button-strong-solid-container-enabled',
          'hover:bg-color-comp-button-strong-solid-container-hover',
          'active:bg-color-comp-button-strong-solid-container-pressed',

          'text-color-comp-button-strong-solid-on-container-enabled',
          'hover:text-color-comp-button-strong-solid-on-container-hover',
          'active:text-color-comp-button-strong-solid-on-container-pressed',
        ],
      },
      {
        variant: 'strong',
        type: 'filled',
        disabled: true,
        selected: false,
        className: [
          'bg-color-comp-button-strong-solid-container-disabled',
          'text-color-comp-button-strong-solid-on-container-disabled',
        ],
      },
      {
        variant: 'strong',
        type: 'filled',
        disabled: false,
        selected: true,
        className: [
          'bg-color-comp-button-strong-solid-container-selected',
          'text-color-comp-button-strong-solid-on-container-selected',
        ],
      },
      // strong / outline
      {
        variant: 'strong',
        type: 'outline',
        disabled: false,
        selected: false,
        className: [
          'bg-color-comp-button-strong-plain-container-enabled',
          'hover:bg-color-comp-button-strong-plain-container-hover',
          'active:bg-color-comp-button-strong-plain-container-pressed',

          'text-color-comp-button-strong-plain-on-container-enabled',
          'hover:text-color-comp-button-strong-plain-on-container-hover',
          'active:text-color-comp-button-strong-plain-on-container-pressed',

          'ring-color-comp-button-strong-plain-border-enabled',
          'hover:ring-color-comp-button-strong-plain-border-hover',
          'active:ring-color-comp-button-strong-plain-border-pressed',
        ],
      },
      {
        variant: 'strong',
        type: 'outline',
        disabled: true,
        selected: false,
        className: [
          'bg-color-comp-button-strong-plain-container-disabled',
          'text-color-comp-button-strong-plain-on-container-disabled',
          'ring-color-comp-button-strong-plain-border-disabled',
        ],
      },
      {
        variant: 'strong',
        type: 'outline',
        disabled: false,
        selected: true,
        className: [
          'bg-color-comp-button-strong-plain-container-selected',
          'text-color-comp-button-strong-plain-on-container-selected',
          'ring-color-comp-button-strong-plain-border-selected',
        ],
      },
      // strong / ghost
      {
        variant: 'strong',
        type: 'ghost',
        disabled: false,
        selected: false,
        className: [
          'bg-color-comp-button-strong-plain-container-enabled',
          'hover:bg-color-comp-button-strong-plain-container-hover',
          'active:bg-color-comp-button-strong-plain-container-pressed',

          'text-color-comp-button-strong-plain-on-container-enabled',
          'hover:text-color-comp-button-strong-plain-on-container-hover',
          'active:text-color-comp-button-strong-plain-on-container-pressed',
        ],
      },
      {
        variant: 'strong',
        type: 'ghost',
        disabled: true,
        selected: false,
        className: [
          'bg-color-comp-button-strong-plain-container-disabled',
          'text-color-comp-button-strong-plain-on-container-disabled',
        ],
      },
      {
        variant: 'strong',
        type: 'ghost',
        disabled: false,
        selected: true,
        className: [
          'bg-color-comp-button-strong-plain-container-selected',
          'text-color-comp-button-strong-plain-on-container-selected',
        ],
      },
      // inverted / filled
      {
        variant: 'inverted',
        type: 'filled',
        disabled: false,
        selected: false,
        className: [
          'bg-color-comp-button-inverted-solid-container-enabled',
          'hover:bg-color-comp-button-inverted-solid-container-hover',
          'active:bg-color-comp-button-inverted-solid-container-pressed',

          'text-color-comp-button-inverted-solid-on-container-enabled',
          'hover:text-color-comp-button-inverted-solid-on-container-hover',
          'active:text-color-comp-button-inverted-solid-on-container-pressed',
        ],
      },
      {
        variant: 'inverted',
        type: 'filled',
        disabled: true,
        selected: false,
        className: [
          'bg-color-comp-button-inverted-solid-container-disabled',
          'text-color-comp-button-inverted-solid-on-container-disabled',
        ],
      },
      {
        variant: 'inverted',
        type: 'filled',
        disabled: false,
        selected: true,
        className: [
          'bg-color-comp-button-inverted-solid-container-selected',
          'text-color-comp-button-inverted-solid-on-container-selected',
        ],
      },
      // inverted / outline
      {
        variant: 'inverted',
        type: 'outline',
        disabled: false,
        selected: false,
        className: [
          'bg-color-comp-button-inverted-plain-container-enabled',
          'hover:bg-color-comp-button-inverted-plain-container-hover',
          'active:bg-color-comp-button-inverted-plain-container-pressed',

          'text-color-comp-button-inverted-plain-on-container-enabled',
          'hover:text-color-comp-button-inverted-plain-on-container-hover',
          'active:text-color-comp-button-inverted-plain-on-container-pressed',

          'ring-color-comp-button-inverted-plain-border-enabled',
          'hover:ring-color-comp-button-inverted-plain-border-hover',
          'active:ring-color-comp-button-inverted-plain-border-pressed',
        ],
      },
      {
        variant: 'inverted',
        type: 'outline',
        disabled: true,
        selected: false,
        className: [
          'bg-color-comp-button-inverted-plain-container-disabled',
          'text-color-comp-button-inverted-plain-on-container-disabled',
          'ring-color-comp-button-inverted-plain-border-disabled',
        ],
      },
      {
        variant: 'inverted',
        type: 'outline',
        disabled: false,
        selected: true,
        className: [
          'bg-color-comp-button-inverted-plain-container-selected',
          'text-color-comp-button-inverted-plain-on-container-selected',
          'ring-color-comp-button-inverted-plain-border-selected',
        ],
      },
      // inverted / ghost
      {
        variant: 'inverted',
        type: 'ghost',
        disabled: false,
        selected: false,
        className: [
          'bg-color-comp-button-inverted-plain-container-enabled',
          'hover:bg-color-comp-button-inverted-plain-container-hover',
          'active:bg-color-comp-button-inverted-plain-container-pressed',

          'text-color-comp-button-inverted-plain-on-container-enabled',
          'hover:text-color-comp-button-inverted-plain-on-container-hover',
          'active:text-color-comp-button-inverted-plain-on-container-pressed',
        ],
      },
      {
        variant: 'inverted',
        type: 'ghost',
        disabled: true,
        selected: false,
        className: [
          'bg-color-comp-button-inverted-plain-container-disabled',
          'text-color-comp-button-inverted-plain-on-container-disabled',
        ],
      },
      {
        variant: 'inverted',
        type: 'ghost',
        disabled: false,
        selected: true,
        className: [
          'bg-color-comp-button-inverted-plain-container-selected',
          'text-color-comp-button-inverted-plain-on-container-selected',
        ],
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'medium',
      type: 'filled',
      disabled: false,
      selected: false,
    },
  },
);
